/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useState, useEffect } from 'react';
import { useLocation, Link, useHistory } from 'react-router-dom';
import './Login.css';
import { useDispatch } from 'react-redux';
import { ToastMessage } from '@client/desktop/constants/toast.constants';
import { deleteAllCookies } from '@client/desktop/utils/deleteCookies';
import config from '@config';
import { GENERIC_ERROR_MESSAGE } from '@client/desktop/constants';
import OmnitureTracking from '@client/desktop/utils/OmnitureTracking';
import Popup from '../../components/Popup/Popup';
import TextInput from '../../components/InputField/TextInput';
import Button from '../../components/Buttons/Button';
// import TnC from '../../components/TnC/TnC';
import { loginAction, setUserAction } from '../../actions/user.actions';
import {
	userIdErrMsg,
	passwordErrMsg,
	defaultErrorDetails,
	LinkCodes,
	changePasswordType,
	passwordRecovery,
	loginText,
	ctaText,
} from './constants';
import { useTitle } from '../../utils/useTitle';
import ForgotPasswordLoadable from '../ForgotPswd';

const Login = () => {
	const location = useLocation();
	const history = useHistory();
	let initialUserId = '';
	if (location && location.state) {
		const { userName = '' } = location.state;
		initialUserId = userName;
	}

	const [step1, setStep1] = useState(true);
	const [step2, setStep2] = useState(false);
	const [loading, setLoading] = useState(false);
	const [userId, setUserId] = useState(initialUserId);
	const [password, setPassword] = useState('');
	const [errorDetails, setErrorDetails] = useState(defaultErrorDetails);
	const [errorText, setErrorText] = useState('');
	const [passwordErr, setPasswordErr] = useState('');
	useTitle('Quest2Travel');

	useEffect(() => {
		deleteAllCookies();
		ForgotPasswordLoadable.preload();
	}, []);

	const dispatch = useDispatch();

	let toast;
	if (location.state?.toast) {
		toast = ToastMessage[location.state.toast];
	}
	const handleStep1 = () => {
		OmnitureTracking({
			event: 'ctaClick',
			page: {
				pageName: 'Login Page: Enter Login ID ',
				siteSection1: 'login',
			},
			click: { ctaName: 'Continue', ctaPosition: window.location.pathname.replaceAll('/', '') },
		});
		event.preventDefault();
		if (!userId) {
			setErrorText(userIdErrMsg);
			return;
		}
		setStep1(!step1);
		setStep2(true);
		dispatch(setUserAction(userId));
	};

	const errorCallback = (errObject) => {
		let primaryError = [...errorDetails];
		if (!errObject) {
			primaryError.message = GENERIC_ERROR_MESSAGE;
		}
		else {
			primaryError = { ...primaryError, ...errObject[0] };
		}

		setLoading(false);
		setErrorDetails(primaryError);
	};
	const OmnitureTrackingData = () => {
		OmnitureTracking({
			event: 'ctaClick',
			page: {
				pageName: 'Login Page: Reset Password',
				siteSection1: 'login',
			},
			click: { ctaName: 'Forgot Password', ctaPosition: window.location.pathname.replaceAll('/', '') },
		});
	};

	const handleStep2 = () => {
		OmnitureTracking({
			event: 'ctaClick',
			page: {
				pageName: 'Login Page: Enter Password ',
				siteSection1: 'login',
			},
			click: { ctaName: 'Continue', ctaPosition: window.location.pathname.replaceAll('/', '') },
		});
		event.preventDefault(); // to prevent refresh
		if (!password || !password.trim()) {
			setPasswordErr(passwordErrMsg);
			return;
		}

		setLoading(true);
		if (userId && password) {
			// get return url from location state or default to home page
			dispatch(loginAction(userId, password, history, errorCallback));
		}
	};

	const handleUserIdInputCallback = (input) => {
		if (input.length > 0 && errorText) {
			setErrorText('');
		}
		setUserId(input);
	};

	const handlePasswordInput = (input) => {
		if (input.length > 0 && passwordErr) {
			setPasswordErr('');
		}
		if (!input) {
			setPasswordErr('');
			setErrorText('');
			setErrorDetails('');
		}
		setPassword(input);
	};

	const getTextLink = () => {
		const { code, links, linkTexts, message } = errorDetails;

		// eslint-disable-next-line no-template-curly-in-string
		if (LinkCodes.includes(code) && message.indexOf('${link}') !== -1) {
			// eslint-disable-next-line no-template-curly-in-string
			const messageArr = message.split('${link}');
			return (
				<p className="fgtLink">
					{' '}
					<span className="lockAcText">
						{' '}
						{messageArr.length > 0 && messageArr[0]}
						{'  '}
					</span>{' '}
					<Link
						to={{ pathname: links[0], state: { userId, type: changePasswordType.RESET_PASSWORD } }}
						className="latoBold capText"
					>
						{' '}
						{linkTexts[0]}
					</Link>{' '}
					<span className="lockAcText">
						{' '}
						{messageArr.length > 1 && messageArr[1]}
						{'  '}
					</span>
				</p>
			);
		}

		return (
			<p className="fgtLink">
				<Link to={{ pathname: '/forgotPassword', state: { userId, type: changePasswordType.FORGOT_PASSWORD } }}>
					<span className="latoBold capText" onClick={OmnitureTrackingData}>
						{passwordRecovery.FORGOT_PASSWORD}
					</span>
				</Link>
			</p>
		);
	};

	const OmnitureCtaTracking = () => {
		OmnitureTracking({
			event: 'ctaClick',
			click: { ctaName: 'Back button', ctaPosition: window.location.pathname.replaceAll('/', '') },
		});
	};
	const handleBackClick = () => {
		OmnitureCtaTracking();
		if (step1) {
			open(config().Q2tGenericHomePageUrl, '_parent');
		}
		if (step2) {
			setStep2(false);
			setStep1(true);
			setErrorDetails(defaultErrorDetails);
		}
	};

	const { code } = errorDetails;

	return (
		<>
			<Popup toast={toast} onBackClick={handleBackClick} addClass="zeroPadding">
				{step1 && (
					<>
						<form className="form" onSubmit={handleStep1}>
							<h2>{loginText.LOGIN_HEADER}</h2>

							<TextInput
								type="text"
								autoComplete="off"
								name="username"
								labelText={loginText.LOGIN_TEXT}
								placeholderText={loginText.PLACEHOLDER_TEXT}
								handleCallback={handleUserIdInputCallback}
								defaultValue={userId}
								maxLength={50}
								trimInput
								autoFocus
							/>

							<Button ButtonText={ctaText.CTA_NEXT} handleBtnClick={handleStep1} disabled={userId.length === 0} />
						</form>
						{/* <TnC /> */}
					</>
				)}

				{step2 && (
					<>
						<form onSubmit={handleStep2}>
							<h2>Enter Password</h2>
							<p className="setPwdDesc">
								Enter the password for <span className="emailFor">{userId}</span>
							</p>
							<TextInput
								type="password"
								labelText="Password"
								placeholderText=""
								name="password"
								autoComplete="off"
								errorClass={errorDetails?.message && !LinkCodes.includes(code) ? 'errorField' : ''}
								errorText={errorDetails?.message}
								handleCallback={handlePasswordInput}
								maxLength={30}
								autoFocus
							>
								{getTextLink()}
							</TextInput>
							<Button
								btnType="buttonPrimary"
								ButtonText={ctaText.CTA_LOGIN}
								handleBtnClick={handleStep2}
								isLoading={loading}
								// disabled in case of link
								disabled={LinkCodes.includes(code) || passwordErr || !password}
							/>
						</form>
					</>
				)}
			</Popup>
		</>
	);
};

export default Login;
